import React, { useRef, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    useTheme,
    Card,
    CardMedia,
    IconButton,
    Dialog,
    DialogContent
} from '@mui/material';
import { CheckCircle, ArrowBackIos, ArrowForwardIos, Close } from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import ZoomIn from '@mui/icons-material/ZoomIn';

const AprtmentPlotsOverview = ({ data }) => {
    const theme = useTheme();
    const apartment = data?.apartment || data || {};
    const amenities = apartment?.Amenities || [];
    const description = apartment?.Description || '';
    const apartmentImg = data?.images || [];
    const scrollRef = useRef(null);

    const [openImage, setOpenImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const videos = data?.videos || [];

    const totalImages = apartmentImg.length;
    const showNavigation = totalImages > 1;

    // Horizontal scroll for gallery arrows
    const scroll = (direction) => {
        if (!scrollRef.current) return;

        const container = scrollRef.current;

        // scroll by one card width
        const scrollAmount = container.offsetWidth * 0.35;

        container.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
    };


    // Open modal
    const handleOpenImage = (img, index) => {
        setOpenImage(img.ImageURL);
        setCurrentImageIndex(index);
    };

    // Close modal
    const handleCloseImage = () => setOpenImage(null);

    // Navigate images in modal
    const handlePrevImage = () => {
        const prevIndex = (currentImageIndex - 1 + totalImages) % totalImages;
        setCurrentImageIndex(prevIndex);
        setOpenImage(apartmentImg[prevIndex].ImageURL); // ✅ Pass URL
    };

    const handleNextImage = () => {
        const nextIndex = (currentImageIndex + 1) % totalImages;
        setCurrentImageIndex(nextIndex);
        setOpenImage(apartmentImg[nextIndex].ImageURL); // ✅ Pass URL
    };


    const getYoutubeEmbedUrl = (url) => {
        if (!url) return '';
        const match = url.match(
            /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/
        );
        return match ? `https://www.youtube.com/embed/${match[1]}` : '';
    };
    return (
        <>
            {/* Amenities */}
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    p: 2,
                    width: { xs: "100%", sm: "100%", lg: "100%" },
                    borderRadius: 2,
                    backgroundColor: "white"
                }}
            >
                <Box mb={3}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        Amenities
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All available facilities in this apartment
                    </Typography>
                </Box>

                {amenities.length > 0 ? (
                    <Grid container spacing={2}>
                        {amenities.map((amenity, index) => (
                            <Grid xs={12} sm={6} md={4} key={index}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 2,
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        '&:hover': { backgroundColor: `${theme.palette.primary.main}08` }
                                    }}
                                >
                                    <CheckCircle sx={{ color: theme.palette.success.main }} />
                                    <Typography variant="body2" fontWeight={500}>
                                        {amenity.name}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center', border: `1px dashed ${theme.palette.divider}`, borderRadius: 2 }}>
                        <Typography color="text.secondary">No amenities information available</Typography>
                    </Box>
                )}
            </Box>

            {/* Description */}
            {description && (
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        p: 2,
                        width: { xs: "100%", sm: "100%", lg: "100%" },
                        borderRadius: 2,
                        backgroundColor: "white",
                        mt: 4
                    }}
                >
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        More About {data?.apartment?.ApartmentName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                </Box>
            )}

            {/* Gallery */}
            {apartmentImg.length > 0 && (
                <Box
                    sx={{
                        p: 3,
                        width: "97%",
                        borderRadius: 3,
                        alignItems: 'center',
                        backgroundColor: "white",
                        mt: 4,
                       ml: { xs: 0, sm: 2, md: 2, lg: 2, xl: 2 },
                        position: 'relative',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        transition: 'box-shadow 0.3s ease',
                        '&:hover': {
                            boxShadow: '0 6px 30px rgba(0,0,0,0.12)'
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        {/* <Box sx={{ width: 4, height: 24, backgroundColor: 'primary.main', mr: 2, borderRadius: 1 }} /> */}
                        <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary' }}>
                            Media Gallery
                        </Typography>
                        <Chip
                            label={`${apartmentImg.length + (videos?.length || 0)} items`}
                            size="small"
                            sx={{
                                ml: 2,
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText',
                                fontWeight: 500
                            }}
                        />
                    </Box>

                    {/* Navigation Arrows */}
                    <IconButton
                        sx={{
                            position: 'absolute',
                            left: {
                                xs: -10,
                                md: -20,
                                lg: -20
                            },
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            backgroundColor: 'white',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                            width: {
                                xs: 40,
                                md: 48,
                                lg: 48
                            },
                            height: 48,
                            '&:hover': {
                                backgroundColor: 'white',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                                transform: 'translateY(-50%) scale(1.05)'
                            },
                            transition: 'all 0.2s ease',
                            // display: { xs: 'none', md: 'flex' }
                        }}
                        onClick={() => scroll('left')}
                    >
                        <ArrowBackIos sx={{ fontSize: 18 }} />
                    </IconButton>

                    <IconButton
                        sx={{
                            position: 'absolute',
                            right: {
                                xs: -10,
                                md: -20,
                                lg: -20
                            },
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            backgroundColor: 'white',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                            width: {
                                xs: 40,
                                md: 48,
                                lg: 48
                            },
                            height: 48,
                            '&:hover': {
                                backgroundColor: 'white',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                                transform: 'translateY(-50%) scale(1.05)'
                            },
                            transition: 'all 0.2s ease',
                            // display: { xs: 'none', md: 'flex' }
                        }}
                        onClick={() => scroll('right')}
                    >
                        <ArrowForwardIos sx={{ fontSize: 18 }} />
                    </IconButton>

                    {/* Gallery Container */}
                    <Box
                        ref={scrollRef}
                        sx={{
                            display: 'flex',
                            gap: 2.5,
                            overflowX: 'auto',
                            scrollBehavior: 'smooth',
                            py: 1,
                            px: 1,
                            '&::-webkit-scrollbar': {
                                height: 6,
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'grey.100',
                                borderRadius: 3,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'grey.300',
                                borderRadius: 3,
                            }
                        }}
                    >
                        {/* Video Card - Modern Style */}
                        {videos?.length > 0 && (
                            <Card
                                sx={{
                                    flexShrink: 0,
                                    borderRadius: 2.5,
                                    overflow: 'hidden',
                                    width: { xs: "85%", sm: "45%", md: "28%", lg: "28%" },
                                    height: 240,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                                    },
                                    border: 'none'
                                }}
                            >
                                <Box sx={{ position: 'relative', height: '100%' }}>
                                    <iframe
                                        src={getYoutubeEmbedUrl(videos[0])}
                                        title="Apartment Video"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 'none' }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 12,
                                            left: 12,
                                            backgroundColor: 'rgba(0,0,0,0.7)',
                                            color: 'white',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 1.5,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            backdropFilter: 'blur(4px)'
                                        }}
                                    >
                                        VIDEO
                                    </Box>
                                </Box>
                            </Card>
                        )}

                        {/* Image Cards - Modern Style */}
                        {apartmentImg.map((img, index) => (
                            <Card
                                key={index}
                                sx={{
                                    flexShrink: 0,
                                    borderRadius: 2.5,
                                    overflow: 'hidden',
                                    width: { xs: "85%", sm: "45%", md: "28%", lg: "28%" },
                                    height: 240,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                                        '& .media-overlay': {
                                            opacity: 1
                                        }
                                    },
                                    border: 'none'
                                }}
                                onClick={() => handleOpenImage(img, index)}
                            >
                                <CardMedia
                                    component="img"
                                    image={img.ImageURL}
                                    alt={`Gallery ${index + 1}`}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transition: 'transform 0.5s ease'
                                    }}
                                />

                                {/* Hover Overlay */}
                                <Box
                                    className="media-overlay"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <IconButton
                                        sx={{
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            '&:hover': {
                                                backgroundColor: 'white',
                                                transform: 'scale(1.1)'
                                            },
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <ZoomIn sx={{ color: 'text.primary' }} />
                                    </IconButton>
                                </Box>

                                {/* Image Number Badge */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        left: 12,
                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                        color: 'text.primary',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {index + 1}
                                </Box>
                            </Card>
                        ))}
                    </Box>

                    {/* Scroll Indicator Dots (Optional) */}
                    {/* <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
            {[1, 2, 3].map((dot) => (
                <Box
                    key={dot}
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: dot === 1 ? 'primary.main' : 'grey.300',
                        transition: 'background-color 0.3s ease'
                    }}
                />
            ))}
        </Box> */}
                </Box>
            )}

            {/* Fullscreen Modal */}
            <Dialog
                open={!!openImage}
                onClose={handleCloseImage}
                maxWidth={false}
                fullScreen={window.innerWidth < 900}
                sx={{
                    '& .MuiDialog-paper': {
                        margin: { xs: 0, md: 2 },
                        maxWidth: '100vw',
                        maxHeight: '100vh',
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.9)',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        backgroundColor: 'rgba(0,0,0,0.9)',
                        position: 'relative'
                    }}
                >
                    {/* Close */}
                    <IconButton
                        onClick={handleCloseImage}
                        sx={{
                            position: 'absolute',
                            top: { xs: 8, md: 16 },
                            right: { xs: 8, md: 16 },
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            zIndex: 10,
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                        }}
                    >
                        <Close />
                    </IconButton>

                    {/* Prev/Next Navigation */}
                    {showNavigation && currentImageIndex > 0 && (
                        <IconButton
                            onClick={handlePrevImage}
                            sx={{
                                position: 'absolute',
                                left: { xs: 8, md: 16 },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                zIndex: 10,
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                            }}
                        >
                            <ArrowBackIos />
                        </IconButton>
                    )}

                    {showNavigation && currentImageIndex < totalImages - 1 && (
                        <IconButton
                            onClick={handleNextImage}
                            sx={{
                                position: 'absolute',
                                right: { xs: 8, md: 16 },
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                zIndex: 10,
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                            }}
                        >
                            <ArrowForwardIos />
                        </IconButton>
                    )}

                    {/* Image */}
                    {openImage && (
                        <Box
                            component="img"
                            src={openImage}
                            alt="Full view"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                cursor: 'zoom-out',
                                display: 'block',
                                margin: 'auto'
                            }}
                            onClick={handleCloseImage}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AprtmentPlotsOverview;